import { useState } from 'react';
import { ADMIN_COLORS } from '../constants/bookingConst';
import { sortTimeSlots, WEEKDAYS, describeSlotSetDays } from '../utils/bookingUtils';

const COLORS = ADMIN_COLORS;

/**
 * One block of opening hours in Config -> Sports & Courts -> Time Slots:
 * the day-of-week toggles, the "add an hour" field, and the chips.
 *
 * Used twice over, which is the whole reason it is its own component:
 *
 *   - the DEFAULT hours, which have no days of their own (they apply to
 *     whatever no set claims) and cannot be removed — `days` is null there,
 *     which hides the day picker and the delete button
 *   - each override set, which does have both
 *
 * Editing hours in two visually different places was how the original slot
 * list ended up with two disagreeing sort orders (see handleAddTimeSlot in
 * AdminConfig.jsx); one component means one behaviour.
 *
 * @param {string} title
 * @param {string} [subtitle] - one line under the title
 * @param {string[]} slots - the hours in this block
 * @param {(slots: string[]) => void} onSlotsChange - always called with the
 *   list already sorted, so stored order matches rendered order
 * @param {number[]|null} [days] - 0-6; null for the default block
 * @param {(days: number[]) => void} [onDaysChange]
 * @param {string} [label] - the set's name
 * @param {(label: string) => void} [onLabelChange]
 * @param {() => void} [onRemove] - omit to make the block undeletable
 * @param {Array<{number: number, label: string}>} [courtOptions] - every
 *   court this set could be scoped to. Omit (or pass none) to hide the court
 *   picker entirely, which is what the default block does.
 * @param {number[]} [courts] - the court numbers this set applies to. An
 *   EMPTY list means every court: that is how a set saved before courts
 *   existed reads, and Firebase cannot store an empty array anyway (see
 *   getSlotSetCourts in bookingUtils.js).
 * @param {(courts: number[]) => void} [onCourtsChange]
 */
function TimeSlotSetEditor({
  title, subtitle, slots = [], onSlotsChange,
  days = null, onDaysChange, label, onLabelChange, onRemove,
  courtOptions = [], courts = [], onCourtsChange,
}) {
  const [draft, setDraft] = useState('');
  const sorted = sortTimeSlots(slots);
  const showDays = Array.isArray(days);

  const addSlot = () => {
    const value = draft.trim();
    if (!value) return;
    // Duplicates would make removal-by-value ambiguous and render as two
    // identical chips the admin can't tell apart — same rule the single
    // list has always had.
    if (slots.includes(value)) { setDraft(''); return; }
    onSlotsChange(sortTimeSlots([...slots, value]));
    setDraft('');
  };

  // By VALUE, never by rendered position: the index comes from the sorted
  // view and would be applied to the stored array, which is exactly how the
  // old editor deleted the wrong slot.
  const removeSlot = (slot) => onSlotsChange(slots.filter(s => s !== slot));

  const toggleDay = (index) => {
    if (!onDaysChange) return;
    const next = days.includes(index) ? days.filter(d => d !== index) : [...days, index];
    onDaysChange(next.sort((a, b) => a - b));
  };

  const showCourts = !!onCourtsChange && courtOptions.length > 0;
  // An empty stored list means "every court", so that is what the boxes
  // show. Ticking every box and storing all of them explicitly is the same
  // thing; unticking the last one comes back here as all-ticked, which is
  // why the hint below says so out loud rather than leaving the admin to
  // discover it.
  const selectedCourts = courts.length > 0 ? courts.map(Number) : courtOptions.map(c => Number(c.number));
  const allCourts = courts.length === 0 || selectedCourts.length === courtOptions.length;

  /**
   * Ticks or unticks one court.
   *
   * The stored value is always the explicit list of what is ticked, built
   * from `selectedCourts` rather than from `courts` — otherwise the first
   * untick on a legacy set (stored empty, rendered all-ticked) would write
   * a list of one instead of a list of all-but-one.
   *
   * @param {number} courtNumber
   * @returns {void}
   */
  const toggleCourt = (courtNumber) => {
    const number = Number(courtNumber);
    const next = selectedCourts.includes(number)
      ? selectedCourts.filter(c => c !== number)
      : [...selectedCourts, number];
    onCourtsChange(next.sort((a, b) => a - b));
  };

  return (
    <div
      style={{
        border: `1px solid ${COLORS.line}`,
        borderRadius: '8px',
        padding: '0.85rem',
        marginBottom: '0.75rem',
        background: COLORS.surface,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {onLabelChange ? (
            <input
              type="text"
              value={label || ''}
              onChange={(e) => onLabelChange(e.target.value)}
              placeholder="Name this set, e.g. Weekends"
              style={{ width: '100%', maxWidth: '260px', fontWeight: 600, fontSize: '0.85rem' }}
            />
          ) : (
            <strong style={{ color: COLORS.ink, fontSize: '0.85rem' }}>{title}</strong>
          )}
          {subtitle && (
            <p style={{ opacity: 0.65, fontSize: '0.72rem', margin: '0.3rem 0 0' }}>{subtitle}</p>
          )}
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            style={{
              background: 'none', border: 'none', color: COLORS.danger,
              cursor: 'pointer', fontSize: '0.75rem', whiteSpace: 'nowrap', padding: 0,
            }}
          >
            Remove set
          </button>
        )}
      </div>

      {showDays && (
        <div style={{ margin: '0.6rem 0' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
            {WEEKDAYS.map(({ index, short, label: full }) => {
              const on = days.includes(index);
              return (
                <button
                  key={index}
                  type="button"
                  title={full}
                  onClick={() => toggleDay(index)}
                  style={{
                    padding: '0.25rem 0.55rem',
                    borderRadius: '999px',
                    cursor: 'pointer',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    border: `1px solid ${on ? COLORS.cyanDeep : COLORS.line}`,
                    background: on ? COLORS.cyanDeep : '#fff',
                    color: on ? '#fff' : COLORS.inkSoft,
                  }}
                >
                  {short}
                </button>
              );
            })}
          </div>
          <p style={{ opacity: 0.6, fontSize: '0.7rem', margin: '0.35rem 0 0' }}>
            Applies on: {describeSlotSetDays({ days })}
            {/* Said here rather than only at save time: a set with days and
                no hours closes the club on those days, which is a real
                setting but not one to discover afterwards. */}
            {days.length > 0 && sorted.length === 0
              && ' — with no hours below, those days will show as closed to guests.'}
          </p>
        </div>
      )}

      {showCourts && (
        <div style={{ margin: '0.6rem 0' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem 0.9rem' }}>
            {courtOptions.map(({ number, label: courtLabel }) => (
              <label
                key={number}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  fontSize: '0.75rem', color: COLORS.inkSoft, cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedCourts.includes(Number(number))}
                  onChange={() => toggleCourt(number)}
                />
                {courtLabel}
              </label>
            ))}
          </div>
          <p style={{ opacity: 0.6, fontSize: '0.7rem', margin: '0.35rem 0 0' }}>
            {allCourts
              ? 'Applies to every court.'
              : `Applies to: ${courtOptions
                .filter(c => selectedCourts.includes(Number(c.number)))
                .map(c => c.label)
                .join(', ')}`}
            {/* The hours a court is NOT given still get a row in the grid,
                because another court is open then — see getTimeSlotsForDate.
                Saying so here stops "why is court 3 full of CLOSED cells?"
                reading as a bug. */}
            {!allCourts && ' — the other courts show those hours as CLOSED.'}
          </p>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', margin: '0.6rem 0 0.6rem' }}>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g., 8-9AM"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSlot(); } }}
          style={{ flex: 1 }}
        />
        <button
          type="button"
          onClick={addSlot}
          style={{
            padding: '0.5rem 0.75rem', background: COLORS.neonLime, color: COLORS.deepBg,
            border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem',
          }}
        >
          Add
        </button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        {sorted.map(slot => (
          <div
            key={slot}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              background: COLORS.cardBg, padding: '0.3rem 0.5rem',
              borderRadius: '4px', border: `1px solid ${COLORS.line}`,
            }}
          >
            <span style={{ color: COLORS.neonLime, fontSize: '0.8rem' }}>{slot}</span>
            <button
              type="button"
              onClick={() => removeSlot(slot)}
              aria-label={`Remove ${slot}`}
              style={{
                background: 'none', border: 'none', color: '#c0392b',
                cursor: 'pointer', fontSize: '0.8rem', lineHeight: 1, padding: 0,
              }}
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {sorted.length === 0 && (
        <p style={{ opacity: 0.6, fontSize: '0.8rem', margin: '0.4rem 0 0' }}>
          {showDays
            ? 'No hours in this set — the days above will show as closed.'
            : 'No time slots configured. Add some above.'}
        </p>
      )}
    </div>
  );
}

export default TimeSlotSetEditor;
